import { ArchiveIcon, ArchiveRestoreIcon } from "lucide-react";
import { useEffect } from "react";
import { Link, Navigate, Outlet, useParams } from "react-router-dom";
import { useAuth } from "@/auth/auth-provider";
import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";
import { lastProjectKey } from "@/hooks/use-current-project";
import { useCan } from "@/hooks/use-permissions";
import { useModelActions, useRecord } from "@/sync/hooks";
import type { Project } from "@/sync/models/project";

/** Gate for `/p/:projectId/*`. A project that vanishes (rejected create, archived elsewhere) exits to the list. */
export function ProjectLayout() {
	const { projectId } = useParams();
	const { workspace } = useAuth();
	const project = useRecord("project", projectId);
	const archived = !!project?.archivedAt;

	useEffect(() => {
		if (project && !archived && workspace)
			localStorage.setItem(lastProjectKey(workspace.id), project.id);
	}, [project, archived, workspace]);

	if (!project) return <Navigate to="/projects" replace />;
	if (archived) return <ArchivedProject project={project} />;
	return <Outlet />;
}

export function HomeRedirect() {
	const { workspace } = useAuth();
	const lastId = workspace ? localStorage.getItem(lastProjectKey(workspace.id)) : null;
	const last = useRecord("project", lastId);
	if (last && !last.archivedAt) return <Navigate to={`/p/${last.id}/overview`} replace />;
	return <Navigate to="/projects" replace />;
}

function ArchivedProject({ project }: { project: Project }) {
	const projects = useModelActions("project");
	const canManage = useCan("projects.manage");
	return (
		<div className="flex flex-1 items-center justify-center p-6">
			<EmptyState
				icon={ArchiveIcon}
				tag="Archived"
				title={`${project.name} is archived`}
				description="Its schedules and syncing are paused. Artifacts and history are kept."
				action={
					<div className="flex gap-2">
						<Button asChild variant="outline">
							<Link to="/projects">Back to projects</Link>
						</Button>
						{canManage && (
							<Button onClick={() => projects.update(project.id, { archivedAt: null })}>
								<ArchiveRestoreIcon />
								Restore
							</Button>
						)}
					</div>
				}
				className="w-full max-w-md"
			/>
		</div>
	);
}
